import React, { memo, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { GuaranteedIcon } from '../../../components/icons';
import Select from '../../../components/form/Select';
import EditUnitBaseModal from '../../../components/modals/EditUnitBaseModal/EditUnitBaseModal';
import ShiftTypeBadge from '../ShiftTypeBadge';
import { GLOBAL_CALENDAR_SHIFT_TYPES } from '../../../constants';

import '../EditUnitModal.scss';

const parseResourceAndSpecialtiesString = (resourceCode, specialtiesArray) => {
	if (!resourceCode) return '';
	const specialtiesString =
		specialtiesArray?.length > 0 ? specialtiesArray.map(({ short_name: shortName }) => shortName).join(', ') : '';
	return `${resourceCode}${specialtiesString.length > 0 ? ' - ' : ''}${specialtiesString}`;
};

const EditUnitModal = ({
	onCancel,
	onEditAssignment,
	setShowModal,
	selectedUnit,
	setSelectedUnit,
	slot,
	availableUnits = [],
	showOnDemandShiftTypeBadges
}) => {
	const [primaryActionDisabled, setPrimaryActionDisabled] = useState(true);
	const unitOptions = availableUnits.map((unit) => ({ label: unit.unit_name, value: unit.id }));
	const emptyLabel = '- Remove Unit -';
	const emptyOption = { label: emptyLabel, value: 0 };

	const onSelectOption = ({ unitId }) => {
		setSelectedUnit(availableUnits.find((_unit) => _unit?.id === unitId));
	};

	const modalTitle = useMemo(() => (slot.unit_id ? 'Update Unit' : 'Add Unit'), [slot]);
	const primaryActionLabel = useMemo(() => (slot.unit_id ? 'Update unit' : 'Add unit'), [slot]);

	useEffect(() => {
		// eslint-disable-next-line camelcase
		const shouldDisable = (selectedUnit?.id || 0) === (slot?.unit_id || 0);
		setPrimaryActionDisabled(shouldDisable);
	}, [selectedUnit, slot]);

	useEffect(() => {
		// eslint-disable-next-line camelcase
		setSelectedUnit(availableUnits.find((_unit) => _unit?.id === slot?.unit_id));
		setPrimaryActionDisabled(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onSaveChanges = () => {
		if (!selectedUnit) {
			setShowModal('UNASSIGN_UNIT_MODAL');
		} else {
			setShowModal(null);
			onEditAssignment();
		}
	};

	const { caregiver, caregiver_name: caregiverName } = slot;
	const { highest_resource_type: resourceType, specialties } = caregiver || {};

	return (
		<EditUnitBaseModal
			title={modalTitle}
			className="edit-unit-modal"
			primaryActionLabel={primaryActionLabel}
			onPrimaryAction={onSaveChanges}
			primaryActionDisabled={primaryActionDisabled}
			secondaryActionLabel="Cancel"
			onSecondaryAction={onCancel}
			onHide={onCancel}
		>
			<div className="shift-details-row">
				<span className="shift-detail">
					Professional: <b>{caregiverName || '-'}</b>
				</span>
			</div>
			<div className="shift-details-row">
				<span className="shift-detail">
					License/Specialties: <b>{parseResourceAndSpecialtiesString(resourceType, specialties) || '-'}</b>
				</span>
			</div>
			<div className="shift-details-row">
				<span className="shift-detail">
					Facility: <b>{slot.facility_name}</b>
				</span>
			</div>
			<div className="shift-details-row">
				<span className="shift-detail">
					Shift details: <b>{moment.utc(slot.raw_start_time).format('dddd, MMM DD, YYYY')}</b>
				</span>
				<span className="shift-detail">
					<b>
						{moment.utc(slot.raw_start_time).format('hh:mm A')} – {moment.utc(slot.raw_end_time).format('hh:mm A')}
					</b>
				</span>
				{slot.is_guaranteed && (
					<span className="shift-detail guaranteed">
						<GuaranteedIcon /> Guaranteed
					</span>
				)}
				{(slot.is_flex || showOnDemandShiftTypeBadges) && (
					<span className="shift-detail shift-type-badge">
						<ShiftTypeBadge
							shiftType={slot.is_flex ? GLOBAL_CALENDAR_SHIFT_TYPES.FLEX : GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND}
						/>
					</span>
				)}
			</div>
			<div className="shift-details-row">
				<span className="shift-detail">
					Shift type: <b>{slot.resource_type}</b>
				</span>
				<span className="shift-detail">
					Unit: <b>{slot.unit_name || '-'}</b>
				</span>
			</div>
			<section>
				<h2>Unit Assignment</h2>
				<Select
					placeholder="Select..."
					name="unitId"
					className="select"
					value={selectedUnit?.id || ''}
					handleChange={onSelectOption}
					options={[emptyOption, ...unitOptions]}
				/>
			</section>
		</EditUnitBaseModal>
	);
};

export default memo(EditUnitModal);
